/**
 * First-run wizard — shown when a tenant has 0 projects. Gets users from
 * empty state to useful work in three deliberate steps.
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Rocket,
  FolderPlus,
  Upload,
  Sparkles,
  CheckCircle2,
  X,
  ArrowRight,
} from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const TEMPLATES = [
  {
    id: 'maa-eu',
    icon: Rocket,
    tone: 'bg-indigo-500/10 text-indigo-600',
    name: 'EU Marketing Authorisation Application',
    type: 'REGULATORY_SUBMISSION',
    markets: ['UK', 'EU'],
    description: 'eCTD Modules 1-5, readiness dashboard, gap analysis, cross-reference QC.',
  },
  {
    id: 'ind-us',
    icon: Rocket,
    tone: 'bg-blue-500/10 text-blue-600',
    name: 'US IND submission',
    type: 'REGULATORY_SUBMISSION',
    markets: ['US'],
    description: 'Module 1 (1571), Module 2 summaries, Module 4/5 study reports.',
  },
  {
    id: 'pv-2026',
    icon: Sparkles,
    tone: 'bg-rose-500/10 text-rose-600',
    name: 'PV programme — 2026',
    type: 'PHARMACOVIGILANCE',
    markets: [],
    description: 'Case intake, MedDRA coding, narratives, periodic safety reports.',
  },
  {
    id: 'psmf-audit',
    icon: CheckCircle2,
    tone: 'bg-teal-500/10 text-teal-600',
    name: 'PSMF inspection readiness',
    type: 'COMPLIANCE_PROGRAMME',
    markets: [],
    description: 'GVP Module II scan, SOP review, gap analysis against audit checklist.',
  },
];

export function FirstRunWizard({ onDismiss }: { onDismiss: () => void }) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selected, setSelected] = useState<typeof TEMPLATES[number] | null>(null);
  const [name, setName] = useState('');
  const [productName, setProductName] = useState('');
  const qc = useQueryClient();
  const navigate = useNavigate();

  const create = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('pick a template');
      return (await api.post('/api/projects', {
        name: name || selected.name,
        type: selected.type,
        productName: productName || undefined,
        targetMarkets: selected.markets,
      })).data;
    },
    onSuccess: (project) => {
      toast.success('Project created', `"${project.name}" is ready for documents`);
      qc.invalidateQueries({ queryKey: ['projects'] });
      onDismiss();
      navigate(`/projects/${project.id}`);
    },
    onError: (err) => toast.error('Create failed', extractApiError(err).message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-2xl rounded-xl border bg-card shadow-2xl animate-slide-up">
        <button onClick={onDismiss} className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:bg-accent" aria-label="Close">
          <X className="h-4 w-4" />
        </button>

        <div className="px-6 pt-6">
          <div className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            <h2 className="font-display text-xl">Welcome to Praxara</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Let's get you set up in two minutes. Pick a template that matches your first project —
            you can create more later.
          </p>
          <div className="mt-4 flex items-center gap-2 text-xs">
            <StepDot n={1} active={step === 1} done={step > 1} label="Template" />
            <div className="h-px flex-1 bg-border" />
            <StepDot n={2} active={step === 2} done={step > 2} label="Details" />
            <div className="h-px flex-1 bg-border" />
            <StepDot n={3} active={step === 3} done={false} label="Ready" />
          </div>
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="grid gap-3 md:grid-cols-2">
              {TEMPLATES.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => { setSelected(t); setName(t.name); setStep(2); }}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border bg-background p-4 text-left transition-all hover:border-primary/40 hover:shadow-sm',
                    )}
                  >
                    <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-md', t.tone)}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium">{t.name}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{t.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {step === 2 && selected && (
            <form onSubmit={(e) => { e.preventDefault(); setStep(3); }} className="space-y-4">
              <div>
                <Label>Project name</Label>
                <Input required value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                <p className="mt-1 text-xs text-muted-foreground">You can rename this later.</p>
              </div>
              <div>
                <Label>Product / drug name (optional)</Label>
                <Input value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="e.g. Drug X 100mg tablets" />
              </div>
              <div className="flex items-center justify-between pt-2">
                <Button type="button" variant="ghost" onClick={() => setStep(1)}>Back</Button>
                <Button type="submit">Next <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Button>
              </div>
            </form>
          )}

          {step === 3 && selected && (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="rounded-md border bg-muted/30 p-4 text-sm">
                <div className="font-medium">{name}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Type: {selected.name}
                  {productName && ` · ${productName}`}
                </div>
              </div>
              <div className="rounded-md border border-primary/20 bg-primary/5 p-4 text-xs text-muted-foreground">
                <p className="font-medium text-foreground">Next steps after creating</p>
                <ul className="mt-2 list-disc space-y-1 pl-4">
                  <li><Upload className="inline h-3 w-3 align-[-1px]" /> Drag a folder or ZIP of documents onto the Documents page — Praxara auto-classifies each file and routes to the right eCTD module.</li>
                  <li><Sparkles className="inline h-3 w-3 align-[-1px]" /> Run a workflow on any document to extract structured data with Gemini 3.1 Pro.</li>
                  <li><CheckCircle2 className="inline h-3 w-3 align-[-1px]" /> Review AI output side-by-side with the PDF, approve with e-signature (21 CFR Part 11).</li>
                </ul>
              </div>
              <div className="flex items-center justify-between">
                <Button type="button" variant="ghost" onClick={() => setStep(2)}>Back</Button>
                <Button type="submit" disabled={create.isPending}>
                  <FolderPlus className="mr-1.5 h-4 w-4" />
                  {create.isPending ? 'Creating...' : 'Create project'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function StepDot({ n, active, done, label }: { n: number; active: boolean; done: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-medium',
          done ? 'bg-primary text-primary-foreground' : active ? 'bg-primary/20 text-primary ring-2 ring-primary' : 'bg-muted text-muted-foreground',
        )}
      >
        {done ? <CheckCircle2 className="h-3 w-3" /> : n}
      </div>
      <span className={cn('text-xs', active ? 'font-medium text-foreground' : 'text-muted-foreground')}>{label}</span>
    </div>
  );
}
